import React, { type JSX, type ForwardedRef, useState } from "react";
import { Box, InputAdornment, TextField, type TextFieldProps } from "@mui/material";
import { NumericFormat, type NumberFormatValues, type SourceInfo } from "react-number-format";

import { useNumberLocale } from "../hooks/useNumberLocale";
import { roundNumber, type RoundingMode } from "../common/numberUtils";

export type LocalizedNumberFieldProps = {
	value: number | null;
	onValueChange: (value: number | null) => void;

	// Formatting options
	decimalScale?: number;
	fixedDecimalScale?: boolean;
	allowNegative?: boolean;
	showThousandSeparator?: boolean;
	decimalSeparator?: string;
	thousandSeparator?: string;

	// Rounding options
	roundingMode?: RoundingMode;

	// Icon support
	icon?: JSX.Element;
	iconBackgroundColor?: string;

	// MUI TextField props
	label?: string;
	error?: boolean;
	helperText?: string;
	disabled?: boolean;
	readOnly?: boolean;
	className?: string;
	fullWidth?: boolean;
	size?: "small" | "medium";
	variant?: "outlined" | "filled" | "standard";
	additionalProps?: Omit<TextFieldProps, "value" | "onChange" | "label" | "error" | "helperText" | "disabled">;
};

type CustomTextFieldProps = TextFieldProps & {
	icon?: JSX.Element;
	iconBackgroundColor?: string;
	readOnly?: boolean;
};

function CustomTextFieldInner(
	{ icon, iconBackgroundColor, readOnly, ...props }: CustomTextFieldProps,
	ref: ForwardedRef<HTMLInputElement>,
): JSX.Element {
	const hasIconBackground = iconBackgroundColor != null;

	return (
		<TextField
			{...props}
			inputRef={ref}
			sx={{
				...((props.sx as object) ?? {}),
				...(hasIconBackground
					? {
							"& .MuiOutlinedInput-root": {
								paddingRight: 0,
							},
						}
					: {}),
			}}
			slotProps={{
				input: {
					readOnly,
					endAdornment: icon ? (
						hasIconBackground ? (
							<Box
								sx={{
									backgroundColor: iconBackgroundColor,
									color: "#fff",
									alignSelf: "stretch",
									display: "flex",
									alignItems: "center",
									padding: "0 12px",
									borderTopRightRadius: "4px",
									borderBottomRightRadius: "4px",
								}}
							>
								{icon}
							</Box>
						) : (
							<InputAdornment position="end">{icon}</InputAdornment>
						)
					) : undefined,
				},
			}}
		/>
	);
}

const CustomTextField = React.forwardRef(CustomTextFieldInner);

export function LocalizedNumberField({
	value,
	onValueChange,
	decimalScale,
	fixedDecimalScale = false,
	allowNegative = true,
	showThousandSeparator = true,
	decimalSeparator: decimalSeparatorProp,
	thousandSeparator: thousandSeparatorProp,
	roundingMode = "halfUp",
	icon,
	iconBackgroundColor,
	label,
	error,
	helperText,
	disabled,
	readOnly,
	className,
	fullWidth,
	size,
	variant,
	additionalProps,
}: LocalizedNumberFieldProps): JSX.Element {
	const locale = useNumberLocale();
	const decimalSeparator = decimalSeparatorProp ?? locale.decimalSeparator;
	const thousandSeparator = thousandSeparatorProp ?? locale.thousandSeparator;
	const [isFocused, setIsFocused] = useState(false);

	const handleValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo): void => {
		// Only update on user input, not on programmatic changes
		if (sourceInfo.source !== "event") return;

		const newValue = values.floatValue ?? null;
		onValueChange(newValue);
	};

	const handleFocus = (): void => {
		setIsFocused(true);
	};

	const handleBlur = (): void => {
		setIsFocused(false);
		// Apply rounding on blur if decimalScale is set
		if (decimalScale !== undefined && value !== null) {
			const rounded = roundNumber(value, decimalScale, roundingMode);
			if (rounded !== value) {
				onValueChange(rounded);
			}
		}
	};

	// Collect TextField props to pass through
	const textFieldProps: Record<string, unknown> = {
		...additionalProps,
		label,
		error,
		helperText,
		disabled,
		readOnly,
		className: className ?? additionalProps?.className,
		fullWidth,
		size,
		variant,
		icon,
		iconBackgroundColor,
	};

	// When focused: allow more decimals for input (to enable rounding like 75,345 → 75,35)
	// When not focused: show the exact decimalScale with fixedDecimalScale for proper display
	const activeDecimalScale = isFocused && decimalScale !== undefined
		? decimalScale + 5
		: decimalScale;
	const activeFixedDecimalScale = isFocused ? false : fixedDecimalScale;

	return (
		<NumericFormat
			value={value ?? ""}
			onValueChange={handleValueChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			decimalSeparator={decimalSeparator}
			thousandSeparator={showThousandSeparator ? thousandSeparator : ""}
			decimalScale={activeDecimalScale}
			fixedDecimalScale={activeFixedDecimalScale}
			allowNegative={allowNegative}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			customInput={CustomTextField as any}
			{...textFieldProps}
		/>
	);
}
